// =============================================================================
// components/home/GetStartedCard.tsx — first-run WELCOME TOUR for CRYB.
//
// A deliberate full-screen onboarding LAYER shown ONCE to new users: a translucent
// glass card materialising over a ParticleBackground + dimming scrim (dim-to-focus,
// skill §12). It orients a new user to the app's areas — Feed, Communities, Chat,
// Profile — as a 4-step guided tour with Back/Next and Skip. It NEVER wedges into
// the scrolling feed: it's a modal layer, dismissed permanently once explored.
//
// Only the final step exposes a real action (Complete profile → EditProfile); every
// other step is orientation only, so nothing is a broken/dead CTA (skill §16).
//
// Lifecycle (UNCHANGED): shows only until the user DISMISSES it (Skip / Explore
// CRYB) or has already completed the profile signal; then it self-hides forever.
// Dismissal + inferred profile-done persist to AsyncStorage under
// 'cryb:getstarted:v1'. Renders null when hidden — the caller drops it in the
// ListHeader unconditionally. Fail-silent on storage errors.
//
// Palette / motion: constants/theme.ts tokens + app glass primitives only.
// =============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Animated,
  AccessibilityInfo,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, IoniconName } from '../ui/Icon';
import { colors, spacing, radius, typographyScale, fonts } from '../../constants/theme';
import { GlassSurface, GlassButton } from '../glass';
import ParticleBackground from '../ui/ParticleBackground';
import { api } from '../../services/api';
import type { RootStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

const STORAGE_KEY = 'cryb:getstarted:v1';

// Persisted shape: whether the card was dismissed, and which items we've inferred
// as done. Only `profile` is cheaply inferable client-side (avatar + bio); the
// tour otherwise is pure orientation with no local completion signal.
interface Persisted {
  dismissed?: boolean;
  done?: { profile?: boolean };
}

// A tour step orients the user to one AREA of the app. Copy is orientation, not a
// link — nothing here deep-links except the final step's explicit secondary action.
interface Step {
  icon: IoniconName;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    icon: 'home',
    title: 'Your feed',
    body: 'Posts, alpha and takes from people you follow. Pull down to refresh anytime.',
  },
  {
    icon: 'people',
    title: 'Communities',
    body: 'Join spaces around your interests. Find them from the Communities tab.',
  },
  {
    icon: 'chatbubble-ellipses',
    title: 'Chat & DMs',
    body: 'Message friends and jump into live voice rooms from the Chat tab.',
  },
  {
    icon: 'person-circle',
    title: 'Make it yours',
    body: 'Complete your profile so people recognise you.',
  },
];

const LAST = STEPS.length - 1;

export default function GetStartedCard() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();

  // `null` = still loading persisted state (render nothing until we know); once
  // loaded, holds the dismissed flag + inferred done-map.
  const [state, setState] = useState<Persisted | null>(null);
  const [step, setStep] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  // Whole-layer materialize (skill §12): scale + opacity in, mirrored on the
  // paged card content when the step changes (interruptible spring from the
  // presentation value — Animated.spring re-targets from the live value).
  const cardEnter = useRef(new Animated.Value(0)).current;   // 0 → 1 layer materialize
  const stepAnim = useRef(new Animated.Value(1)).current;    // per-step content swap

  // Load persisted state, then cheaply infer profile completion from /auth/me
  // (avatar + bio). Any storage/network failure leaves the tour in its default
  // (shown) — never crashes the header.
  useEffect(() => {
    let active = true;
    (async () => {
      let persisted: Persisted = {};
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        if (raw) persisted = JSON.parse(raw) || {};
      } catch { /* fail-silent: default to shown */ }

      // Cheap profile-done inference: avatar AND bio both present.
      let profileDone = persisted.done?.profile ?? false;
      if (!profileDone) {
        try {
          const res = await api<{ user?: any }>('/auth/me');
          const me: any = res.success ? (res.data?.user ?? res.data) : null;
          const hasAvatar = !!(me?.avatar ?? me?.avatar_url ?? me?.avatarUrl);
          const hasBio = !!(me?.bio && String(me.bio).trim().length > 0);
          if (hasAvatar && hasBio) profileDone = true;
        } catch { /* non-fatal */ }
      }

      if (!active) return;
      setState({ ...persisted, done: { ...persisted.done, profile: profileDone } });
    })();
    return () => { active = false; };
  }, []);

  // Honor reduced motion (skill §14): plain cross-fade fallback, no spring/scale.
  useEffect(() => {
    let active = true;
    AccessibilityInfo.isReduceMotionEnabled().then((v) => { if (active) setReduceMotion(!!v); }).catch(() => {});
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', (v) => setReduceMotion(!!v));
    return () => { active = false; sub?.remove?.(); };
  }, []);

  // Materialize the layer once it becomes visible (skill §12: scale + opacity,
  // not a flat fade). Reduced motion → snap to 1 (the Modal's fade handles it).
  const visible = !!state && !state.dismissed && !(state.done?.profile);
  useEffect(() => {
    if (!visible) return;
    cardEnter.setValue(reduceMotion ? 1 : 0);
    if (reduceMotion) return;
    Animated.spring(cardEnter, {
      toValue: 1,
      useNativeDriver: true,
      damping: 18,      // critically-damped settle (skill §4), no overshoot on a fade-in
      stiffness: 180,
      mass: 1,
    }).start();
  }, [visible, reduceMotion, cardEnter]);

  // Interruptible per-step content swap: animate stepAnim from its PRESENTATION
  // value (Animated.spring re-targets live), so rapid Next/Back never jumps
  // (skill §3/§4, response ~0.35 → stiffness ~200, damping 1.0-ish).
  const goToStep = useCallback((next: number) => {
    setStep(next);
    if (reduceMotion) { stepAnim.setValue(1); return; }
    stepAnim.setValue(0);
    Animated.spring(stepAnim, {
      toValue: 1,
      useNativeDriver: true,
      damping: 20,
      stiffness: 200,
      mass: 1,
    }).start();
  }, [reduceMotion, stepAnim]);

  // Best-effort persist (fire-and-forget, fail-silent).
  const persist = useCallback((next: Persisted) => {
    AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(next)).catch(() => {});
  }, []);

  const handleDismiss = useCallback(() => {
    setState((prev) => {
      const next: Persisted = { ...(prev ?? {}), dismissed: true };
      persist(next);
      return next;
    });
  }, [persist]);

  const handleNext = useCallback(() => {
    if (step < LAST) goToStep(step + 1);
    else handleDismiss();
  }, [step, goToStep, handleDismiss]);

  const handleBack = useCallback(() => {
    if (step > 0) goToStep(step - 1);
  }, [step, goToStep]);

  const handleCompleteProfile = useCallback(() => {
    navigation.navigate('EditProfile');
    handleDismiss();
  }, [navigation, handleDismiss]);

  // Still resolving persisted state — render nothing to avoid a flash.
  if (!state) return null;
  // Dismissed → gone forever.
  if (state.dismissed) return null;
  // Profile already complete → the tour has served its purpose; self-hide.
  if (state.done?.profile) return null;

  const current = STEPS[step];
  const isLast = step === LAST;

  // Card materialize transform (skill §12): scale + opacity together.
  const cardStyle = {
    opacity: cardEnter,
    transform: [
      { scale: cardEnter.interpolate({ inputRange: [0, 1], outputRange: [0.94, 1] }) },
    ],
  };
  // Per-step content: fade + a small rise from the presentation value.
  const contentStyle = {
    opacity: stepAnim,
    transform: [
      { translateY: stepAnim.interpolate({ inputRange: [0, 1], outputRange: [10, 0] }) },
    ],
  };

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={handleDismiss}
    >
      <View style={StyleSheet.absoluteFill}>
        {/* Full-bleed animated backdrop under the status bar (skill §12) */}
        <ParticleBackground theme="light" count={5} intensity={0.9} />
        {/* Dim-to-focus scrim — this is a guided step; taps do nothing (explicit Skip) */}
        <View style={styles.scrim} pointerEvents="none" />

        <SafeTour>
          {/* TOP ROW: segmented progress + Skip tour */}
          <View style={[styles.topRow, { paddingTop: insets.top + spacing.md }]}>
            <View style={styles.segments}>
              {STEPS.map((_, i) => (
                <View key={i} style={styles.segTrack}>
                  <View style={[styles.segFill, i <= step && styles.segFillActive]} />
                </View>
              ))}
            </View>
            <TouchableOpacity
              onPress={handleDismiss}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Skip tour"
            >
              <Text style={styles.skip}>Skip tour</Text>
            </TouchableOpacity>
          </View>

          {/* CENTER: paged glass card (content swaps on step change) */}
          <View style={styles.center}>
            <Animated.View style={cardStyle}>
              <GlassSurface variant="card" radius={radius.xl} style={styles.card} contentStyle={styles.cardContent}>
                <Animated.View style={contentStyle}>
                  <View style={styles.medallion}>
                    <Ionicons name={current.icon} size={26} color={colors.primary} />
                  </View>
                  <Text style={styles.title}>{current.title}</Text>
                  <Text style={styles.body}>{current.body}</Text>

                  {/* Final step ONLY: a real secondary action (no dead links elsewhere) */}
                  {isLast && (
                    <View style={styles.secondaryWrap}>
                      <GlassButton
                        title="Complete profile"
                        variant="secondary"
                        size="sm"
                        icon="person-circle-outline"
                        onPress={handleCompleteProfile}
                      />
                    </View>
                  )}
                </Animated.View>
              </GlassSurface>
            </Animated.View>
          </View>

          {/* BOTTOM CTA: [Back] + primary Next / Explore CRYB */}
          <View style={[styles.ctaRow, { paddingBottom: insets.bottom + spacing.md }]}>
            {step > 0 && (
              <TouchableOpacity
                style={styles.backBtn}
                onPress={handleBack}
                accessibilityRole="button"
                accessibilityLabel="Back"
              >
                <Ionicons name="chevron-back" size={22} color={colors.ink2} />
              </TouchableOpacity>
            )}
            <View style={styles.ctaFlex}>
              <GlassButton
                title={isLast ? 'Explore CRYB' : 'Next'}
                variant="primary"
                size="lg"
                fullWidth
                onPress={handleNext}
              />
            </View>
          </View>
        </SafeTour>
      </View>
    </Modal>
  );
}

// Small local wrapper so the tour respects safe areas without pulling SafeAreaView
// styling into the render tree above. We already apply insets manually per row.
function SafeTour({ children }: { children: React.ReactNode }) {
  return <View style={styles.safe}>{children}</View>;
}

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(14,11,26,0.55)',
  },
  safe: {
    flex: 1,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  segments: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
  },
  segTrack: {
    flex: 1,
    height: 4,
    borderRadius: radius.full,
    backgroundColor: 'rgba(255,255,255,0.28)',
    overflow: 'hidden',
  },
  segFill: {
    flex: 1,
    borderRadius: radius.full,
    backgroundColor: 'transparent',
  },
  segFillActive: {
    backgroundColor: colors.primary,
  },
  skip: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.semibold,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
  },
  card: {
    marginHorizontal: spacing.lg,
  },
  cardContent: {
    padding: spacing.xl,
    alignItems: 'flex-start',
  },
  medallion: {
    width: 56,
    height: 56,
    borderRadius: radius.lg,
    backgroundColor: colors.primary + '14',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    ...typographyScale.h2,      // 24 / 700 / -0.3 tracking (tightened large text, skill §15)
    color: colors.ink,
    marginTop: spacing.lg,
  },
  body: {
    ...typographyScale.body,    // near-0 tracking, lineHeight 22 (size-specific, skill §15)
    color: colors.ink2,
    marginTop: spacing.sm,
    lineHeight: 22,
  },
  secondaryWrap: {
    marginTop: spacing.lg,
    alignSelf: 'stretch',
  },
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  backBtn: {
    width: 50,
    height: 54,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.14)',
  },
  ctaFlex: {
    flex: 1,
  },
});
